import type {
  BroadcastContent,
  ConnectingDelayJudgementMode,
  ConnectingLineDelayProperties,
} from '../../../api/types';
import type { IStationsLine } from '../../../data/istationsLines';
import type { RouteMaster } from '../../../data/routes';
import { getRouteColor } from '../../../data/routes';
import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';

import {
  PHRASE_TEMPLATE_CONNECTING_DELAY_SECTION_SCHEDULED,
  PHRASE_TEMPLATE_CONNECTING_DELAY_WHOLE_LINE,
} from '../../../data/phraseTemplates';
import { usePhraseTemplateDefaults } from '../../../contexts/PhraseTemplateContext';
import {
  CONNECTING_SINGLE_SSML_DEFAULT,
  crossesDepartureForecastBoundary,
  DEFAULT_CONNECTING_DELAY_PATTERNS,
  DEFAULT_DEPARTURE_FORECAST_PATTERNS,
  isDepartureForecastMode,
  resolveEffectiveSsml,
  resolveLocalizedDefaults,
} from '../../../utils/broadcastContents';
// 数値入力は共通の NumericInput を使う。
// 素の <input type="number" value={number}> だと Number('') === 0 のため
// 既定値 0 を消せない不具合が起きる (NumericInput 側のコメント参照)。
import { clampNonNegativeInt, NumericInput } from '../../NumericInput';
import { CONNECTING_DELAY_MODE_META } from '../constants';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import { LocalizedRouteNicknameFields } from '../parts/LocalizedRouteNicknameFields';
import { RouteNameGuide } from '../parts/RouteNameGuide';
import { LocalizedTextFields } from '../parts/LocalizedTextFields';
import {
  isRemovableLocalizedBody,
  localizedConnectingSingleDefaults,
} from '../../../utils/localizedSsml';
import { DepartureForecastIStationsWarning } from '../parts/DepartureForecastIStationsWarning';
import { IStationsLinesSelect } from '../parts/IStationsLinesSelect';
import { StopSelect } from '../parts/StopSelect';
import { syncRouteNickname } from '../../../utils/routeNicknameDefaults';

import {
  CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS,
  CONNECTING_WHOLE_LINE_PLACEHOLDERS,
} from '../placeholders';
import { deriveContentSampleOverrides } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { ConnectingLineDelayPatternsTable } from './ConnectingLineDelayPatternsTable';

// TODO(refactor-followup): 判定モード (whole_line / section_scheduled / transfer_aware) ごとの
// 表示切替が単一の JSX ツリーと onChange クロージャに密結合しているため、本ファイルは
// 目安 (400 行) を超えている。サブコンポーネント分割には `onChange` へ渡す properties の
// 生成経路 (props 全体をスプレッドして必須項目を補う形) の共通化が必要で、
// 親へ返すオブジェクトの形が変わるリスクがあるため別 PR で対応する。
export function ConnectingLineDelayFields({
  props,
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  routes,
  routeStops,
  istationsLines = [],
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  /**
   * プレビュー対象の放送コンテンツ (Issue #439)。
   * サーバ側でこの内容をレンダリングして実放送と同一の文面を合成する。
   */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  /**
   * 「運行情報の参照元（アイステイションズ路線）」の選択肢マスタ。
   * ATOS 線区マスタ (`routes`) とは独立した別系統で、連動させない。
   */
  istationsLines?: IStationsLine[];
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  const threshold = props.delay_threshold_seconds ?? 0;
  const languages = content?.languages ?? ['ja'];
  const currentRouteId = props.route_id ?? '';
  // 判定モード。未指定 (既存データ) は接続線全体 (whole_line) として表示する。
  const judgementMode: ConnectingDelayJudgementMode = props.delay_judgement_mode ?? 'whole_line';
  const transferTimeSeconds = props.transfer_time_seconds ?? 180;

  // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
  // 区間 (start/end_stop_id) / 乗換駅 (transfer_stop_id) / 方面 (direction_id) は
  // 判定にも放送文にも使用しないため、UI からも非表示にする。
  // transfer_aware モードでは「区間 (開始/終了駅)」は判定にも放送文にも使われないため
  // 同様に UI から非表示にする (乗換駅・方面・乗換時間で判定する純化されたモード)。
  const isWholeLineMode = judgementMode === 'whole_line';
  const isTransferAwareMode = judgementMode === 'transfer_aware';
  // SBI-5: 発車の見込み。transfer_aware と同じく乗換駅・乗換時間・方面と
  // 遅延パターン表 (ただし粒度の列は隠す) で設定する別モード。
  const isDepartureForecast = isDepartureForecastMode(judgementMode);
  const usesPatternsTable = isTransferAwareMode || isDepartureForecast;
  const showSectionFields = judgementMode === 'section_scheduled'; // 開始駅 / 終了駅
  const showTransferStopField = !isWholeLineMode; // 乗換駅
  const showDirectionField = !isWholeLineMode; // 方面

  // transfer_aware モードでは「接続線遅延しきい値 (秒)」と単一 SSML テンプレートは使わず、
  // 自列車遅延と同じ表形式 (遅れ分数の幅 × 放送粒度 × 放送パターン) で設定する。
  // これにより遅れ分数の範囲ごとに放送内容を切り替えられる。
  const showThresholdField = !usesPatternsTable;
  const showSingleSsmlField = !usesPatternsTable;
  const showDelayPatternsTable = usesPatternsTable;

  /*
   * Issue #552: 単一 SSML テンプレート (whole_line / section_scheduled) の
   * 「未入力時に実際に放送される文面」。
   *
   * 定型文マスタ (判定モード別 ID) → コード内既定 の順に解決し、
   * placeholder (薄字) と多言語欄の既定値に使う。放送側
   * (`_render_connecting_delay()`) と同じ template_id を引くため、
   * 「薄字 = 実際に放送される文面」が成立する。
   */
  const resolveDefault = usePhraseTemplateDefaults();
  const singleSsmlMode = isWholeLineMode ? 'whole_line' : 'section_scheduled';
  const singleSsmlTemplateId = isWholeLineMode
    ? PHRASE_TEMPLATE_CONNECTING_DELAY_WHOLE_LINE
    : PHRASE_TEMPLATE_CONNECTING_DELAY_SECTION_SCHEDULED;
  const effectiveSingleSsml = resolveEffectiveSsml(
    props.ssml_template,
    singleSsmlTemplateId,
    CONNECTING_SINGLE_SSML_DEFAULT[singleSsmlMode],
    resolveDefault,
  );

  // マスタに無い route_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownRoute =
    currentRouteId !== '' && !routes.some((r) => r.route_id === currentRouteId);

  // 選択中の接続線 (route_id) に対応する駅一覧 (親が事前取得した routeStops から取り出す)。
  // 念のため stop_sequence 昇順に整列する。
  const stops = sortStopsBySequence(currentRouteId ? routeStops[currentRouteId] : []);

  const startStopId = props.start_stop_id ?? '';

  const endStopId = props.end_stop_id ?? '';

  const transferStopId = props.transfer_stop_id ?? '';

  const directionId = props.direction_id ?? '';

  // 案内文面 (SSML) の {direction_name} プレースホルダに差し込む自由入力テキスト。
  // 判定用 direction_id とは独立した「放送文専用の方面文言」。
  const directionName = props.direction_name ?? '';

  // 方面ドロップダウンの選択肢は、選択中の接続線 (route_id) の方向マスタ (directions) から生成する。
  // direction_id は routes-master.json の各方面に明示された `direction_id` フィールドを
  // 優先採用し、保存値とする。未指定 (古いデータ) の場合は後方互換として配列順 (index) を
  // 文字列化した値 ("0", "1", ...) にフォールバックする。
  //
  // 例) 中総緩は Transformer 側の解釈が他路線と逆 (= 0 が三鷹方面、1 が千葉方面) のため、
  // routes-master.json で `direction_id: "0"` を「B線（三鷹方面）」に、
  // `direction_id: "1"` を「A線（千葉方面）」に明示しており、本フィールドを参照することで
  // UI 上で「A線（千葉方面）」を選択したときに `direction_id="1"` (= Transformer 側の千葉) が
  // 保存されるようになる。
  const currentRoute = routes.find((r) => r.route_id === currentRouteId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  // マスタに無い direction_id (古いデータ・別線区への参照など) も選択肢として残し、
  // 既存値が消えてしまわないようにする
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  // 表示用: 接続線の路線カラー / 線名 (タブ見出しに使う)。
  // part1 (旧「接続線遅延」) は 1 コンテンツ = 1 接続線のデータモデルなので、
  // part2 と同じ見た目のタブを 1 枚だけ表示して UI を統一する。
  // (将来 part1 が複数接続線を持つようになってもタブ列がそのまま増やせる形にしておく)
  const part1TabColor = getRouteColor(routes, currentRouteId);
  const part1TabLabel = currentRoute?.route_name || currentRouteId || '接続線 (未選択)';

  return (
    <div className="contents-fields">
      {/*
        接続線タブ (part1)。接続線は 1 本なのでタブは 1 枚だが、part2 と同じ
        `connecting-line-tabs` / `connecting-line-tab` のスタイルを使い、
        タブの色を路線カラー (routes-master.json の route_color) で塗る。
      */}
      <div className="connecting-line-tabs" role="tablist" data-testid="connecting-line-part1-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={true}
          data-testid="connecting-line-part1-tab"
          className="connecting-line-tab is-active"
          style={{
            background: part1TabColor.background,
            color: part1TabColor.text,
            borderBottomColor: part1TabColor.background,
          }}
          // 接続線が 1 本しかないため常に選択中。クリックしても切り替え先が無いので
          // 見出しとしてのみ機能させる (aria-selected は常に true)。
          onClick={() => undefined}
        >
          <span
            className="connecting-line-tab__swatch"
            style={{ background: part1TabColor.background }}
            aria-hidden="true"
          />
          #1 {part1TabLabel}
        </button>
      </div>
      <label>
        <span className="required">接続線</span>
        <select
          aria-label="接続線 route_id"
          value={currentRouteId}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: e.target.value,
              delay_threshold_seconds: threshold,
              // 路線を変えたら区間 (開始/終了駅) と乗換駅はリセットする
              // (別路線の stop_id が残らないように)
              start_stop_id: '',
              end_stop_id: '',
              transfer_stop_id: '',
              // 線区愛称は、自動入力のままなら新しい線区のマスタ名称へ追従させる
              // (手入力した愛称は消さない。規則は `syncRouteNickname` を参照)。
              ...syncRouteNickname(currentRouteId, e.target.value, props, routes, languages),
            } as ConnectingLineDelayProperties)
          }
        >
          <option value="">(選択してください)</option>
          {routes.map((r) => (
            <option key={r.route_id} value={r.route_id}>
              {r.route_name || r.route_id}
            </option>
          ))}
          {isUnknownRoute ? (
            <option value={currentRouteId}>{currentRouteId} (一覧にない)</option>
          ) : null}
        </select>
      </label>
      {/*
        接続線の線区愛称 (必須)。放送 SSML の {route_name} / {route} プレースホルダに
        最優先で使用される。線区を選ぶと線区一覧の名称が自動入力される (手入力で上書き可)。
        空欄の保存は画面のバリデーションで止める (API は既存データ互換のため必須にしない)。
      */}
      <label>
        <span className="required">接続線 線区愛称</span>
        <input
          type="text"
          aria-label="接続線 線区愛称"
          data-testid="connecting-line-route-nickname"
          value={typeof props.route_nickname === 'string' ? props.route_nickname : ''}
          onChange={(e) =>
            onChange({
              ...props,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: threshold,
              route_nickname: e.target.value,
            } as ConnectingLineDelayProperties)
          }
          placeholder="例: 湘南新宿ライン"
        />
        <span className="hint" style={{ fontSize: 12, color: '#555' }}>
          放送の <code>{'{route_name}'}</code> / <code>{'{route}'}</code>{' '}
          に使われる線名です。線区を選ぶと自動で入力されます（変更もできます）。
        </span>
      </label>
      {/*
        Issue #579 (SBI-09): 接続線の線区愛称の **各言語名称**。
        放送言語に日本語以外が選ばれているときのみ表示する。
        線区を選ぶと線区一覧の名称が自動入力される。空欄のまま保存はできない。
      */}
      <LocalizedRouteNicknameFields
        label="接続線 線区愛称"
        required
        languages={languages}
        value={props.route_nickname_i18n}
        japaneseNickname={typeof props.route_nickname === 'string' ? props.route_nickname : ''}
        testIdPrefix={`connecting-line-route-nickname-${contentIndex}`}
        onChange={(value) =>
          onChange({
            ...props,
            route_id: props.route_id ?? '',
            delay_threshold_seconds: threshold,
            route_nickname_i18n: value,
          } as ConnectingLineDelayProperties)
        }
      />
      {/* 愛称が未設定のとき、放送で実際に案内される線名を言語ごとに示す。 */}
      <RouteNameGuide
        testId="connecting-line-route-name-guide"
        routeId={currentRouteId}
        nickname={props.route_nickname}
        nicknameI18n={props.route_nickname_i18n}
        languages={languages}
      />
      {/*
          運行情報の参照元 (アイステイションズ路線) の複数選択。
          part1 は lines[] を持たないフラット構造なので properties 直下
          (`istations_lines`) に保持する。上の「接続線 route_id」(ATOS) とは
          連動させず、一致しない路線も自由に選べる。
        */}
      <IStationsLinesSelect
        testIdPrefix="connecting-istations-lines"
        options={istationsLines}
        value={Array.isArray(props.istations_lines) ? props.istations_lines : []}
        onChange={(nextLines) =>
          onChange({
            ...props,
            route_id: props.route_id ?? '',
            delay_threshold_seconds: threshold,
            // 空配列は「未設定」として properties から落とし、DB / JSON に
            // ゴミを残さない (サーバ側も空配列はキーごと保存しない)。
            istations_lines: nextLines.length > 0 ? nextLines : undefined,
          } as ConnectingLineDelayProperties)
        }
      />
      {/* SBI-5: 発車見込み案内で「運行情報と連携」が未設定のときの警告 (入力は必須にしない) */}
      {isDepartureForecast ? (
        <DepartureForecastIStationsWarning
          istationsLines={props.istations_lines}
          testId="connecting-departure-forecast-istations-warning"
        />
      ) : null}

      {/*
        接続線遅延しきい値 (秒) は whole_line / section_scheduled の単一閾値判定で使用する。

        transfer_aware は遅れ分数の幅ごとに粒度を切り替える delay_patterns で判定するため
        非表示にする (※ 後方互換のため値自体は properties に残す)。
      */}
      {showThresholdField ? (
        <label>
          <span className="required">接続線遅延しきい値 (秒)</span>
          <NumericInput
            min={0}
            step={1}
            aria-label="接続線遅延しきい値 (秒)"
            value={threshold}
            normalizeValue={clampNonNegativeInt}
            onChange={(nextThreshold) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: nextThreshold,
              } as ConnectingLineDelayProperties)
            }
          />
        </label>
      ) : null}
      <label>
        <span className="required">判定モード</span>
        <select
          aria-label="判定モード"
          value={judgementMode}
          onChange={(e) => {
            const nextMode = e.target.value as ConnectingDelayJudgementMode;
            // whole_line モードは「接続線全体の最大遅延」のみで判定する純化されたモード。
            // 切り替え時に区間 / 乗換駅 / 方面 の旧入力を properties から落として、
            // DB / JSON にゴミとして残らないようにする。
            const cleared =
              nextMode === 'whole_line'
                ? {
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                    transfer_stop_id: undefined,
                    direction_id: undefined,
                    // 案内文面用の方面文言も whole_line では使用しないので落とす
                    direction_name: undefined,
                  }
                : {};
            // transfer_aware モードに切り替えたとき delay_patterns が未設定なら、
            // 自列車遅延と同等のデフォルトパターン (1分以上〜10分未満 / 5分単位 / ...) を
            // 注入する。既存の delay_patterns があればそのまま尊重する。
            // また transfer_aware では単一 SSML テンプレート / 単一しきい値は使用しないため、
            // それ以外のモードからの切替時にデータを落として DB に残らないようにする。
            // さらに transfer_aware は区間 (開始/終了駅) を判定にも放送文にも使用しないため、
            // section_scheduled から切り替えてきた際に残りがちな start_stop_id / end_stop_id も
            // 落として DB / JSON にゴミとして残らないようにする。
            //
            // SBI-5: transfer_aware と departure_forecast は同じ遅延パターン表を使うが、
            // パターンの意味 (「放送粒度」⇔「案内する遅れ分数の範囲」) と行の文面に互換性が無い。
            // 両者をまたぐ切替では、遅延パターンを新モードの既定へ戻す。
            const crossesForecast = crossesDepartureForecastBoundary(judgementMode, nextMode);
            const transferPatch: Partial<ConnectingLineDelayProperties> =
              nextMode === 'transfer_aware' || nextMode === 'departure_forecast'
                ? {
                    delay_patterns:
                      Array.isArray(props.delay_patterns) && !crossesForecast
                        ? props.delay_patterns
                        : (nextMode === 'departure_forecast'
                            ? DEFAULT_DEPARTURE_FORECAST_PATTERNS
                            : DEFAULT_CONNECTING_DELAY_PATTERNS
                          ).map((p) => ({ ...p })),
                    // transfer_aware は単一 SSML テンプレートを使わないので落とす
                    ssml_template: undefined,
                    ssml_template_i18n: undefined,
                    // 区間 (開始/終了駅) は transfer_aware では使用しないので落とす
                    start_stop_id: undefined,
                    end_stop_id: undefined,
                  }
                : {};

            // 単一 SSML テンプレート (whole_line / section_scheduled 用) のモード切替処理。
            //
            // Issue #527: 「空欄 = 定型文マスタへ委譲」へ規約変更したため、
            // **モード別デフォルト文を実値で書き込むのは止めた**。
            // 実値を焼き込むと個別文面がマスタより優先され、
            // 「マスタ編集で既存コンテンツの放送内容が一括で変わる」が成立しなくなる。
            //
            // 代わりに、現在の値が「既知のモード別デフォルト文と完全一致」なら
            // ユーザ未編集とみなして **空欄 (= マスタ委譲) へ戻す**。
            // 放送側は判定モードに応じた template_id
            // (`connecting_delay_whole_line` / `connecting_delay_section_scheduled`) を
            // 引くため、これで「モードに合った文面」が自動的に選ばれる。
            // ユーザがカスタム編集した文言はそのまま尊重する。
            const KNOWN_SINGLE_SSML_DEFAULTS: ReadonlySet<string> = new Set<string>([
              CONNECTING_SINGLE_SSML_DEFAULT.whole_line,
              CONNECTING_SINGLE_SSML_DEFAULT.section_scheduled,
            ]);
            const singleSsmlPatch: Partial<ConnectingLineDelayProperties> =
              nextMode !== 'transfer_aware' &&
              nextMode !== 'departure_forecast' &&
              typeof props.ssml_template === 'string' &&
              KNOWN_SINGLE_SSML_DEFAULTS.has(props.ssml_template)
                ? { ssml_template: undefined }
                : {};
            /*
             * 言語別文面 (`ssml_template_i18n`) のモード切替処理 — Issue #556。
             *
             * 以前はモード別のコード内既定訳を **値として投入** していたため、
             * 判定モードを触っただけで個別文面が焼き込まれ、
             * 「定型文マスタで英中韓を整備しても放送に反映されない」状態になっていた。
             *
             * 日本語 (`singleSsmlPatch`) と同じ規則に揃え、
             * **既知の既定訳と完全一致する値は空欄 (= マスタ委譲) へ戻す**。
             * 運用者がカスタム編集した訳文はそのまま温存する。
             */
            const localizedSsmlPatch: Partial<ConnectingLineDelayProperties> = (() => {
              if (nextMode === 'transfer_aware' || nextMode === 'departure_forecast') {
                // transfer_aware / departure_forecast は単一テンプレートを使わないので落とす (従来どおり)。
                return { ssml_template_i18n: undefined };
              }
              const current = props.ssml_template_i18n;
              if (!current) return {};
              const kept = Object.fromEntries(
                Object.entries(current).filter(([, text]) => !isRemovableLocalizedBody(text)),
              );
              return {
                ssml_template_i18n: Object.keys(kept).length > 0 ? kept : undefined,
              };
            })();
            onChange({
              ...props,
              ...cleared,
              ...transferPatch,
              ...singleSsmlPatch,
              ...localizedSsmlPatch,
              route_id: props.route_id ?? '',
              delay_threshold_seconds: threshold,
              delay_judgement_mode: nextMode,
            } as ConnectingLineDelayProperties);
          }}
        >
          {CONNECTING_DELAY_MODE_META.map((m) => (
            <option key={m.mode} value={m.mode}>
              {m.label}
            </option>
          ))}
        </select>
      </label>

      <p className="hint" style={{ margin: '4px 0' }}>
        {judgementMode === 'whole_line'
          ? '接続線全体の最大遅延のみで放送可否を判定します。区間や乗換駅・方面の指定は本モードでは使用しません (他モードに任せてください)。'
          : judgementMode === 'section_scheduled'
            ? '指定区間に定刻在線しているはずの便の遅延で判定します。開始駅・終了駅が必須です。'
            : judgementMode === 'departure_forecast'
              ? 'お客さまがまさに乗り換えようとしている接続列車 (自列車到着＋乗換時間以降に発車する便) について、遅延分数ではなく「発車の見込み時刻」を案内します。乗換駅・乗換時間が必須です。'
              : 'お客さまがまさに乗り換えようとしている接続列車 (自列車到着＋乗換時間以降に発車する便) の遅延で判定します。乗換駅・乗換時間が必須です。'}
      </p>
      {/*
        区間 (開始/終了駅) は section_scheduled でのみ使用する。
        whole_line では「接続線全体の最大遅延」のみで判定する純化されたモードのため非表示。
        transfer_aware は区間ではなく乗換駅で判定するため非表示。
      */}
      {showSectionFields ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定開始駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定開始駅"
                testId="delay-start-stop-select"
                value={startStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    start_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className={judgementMode === 'section_scheduled' ? 'required' : undefined}>
                遅延判定終了駅{judgementMode === 'section_scheduled' ? '' : ' (任意)'}
              </span>
              <StopSelect
                ariaLabel="遅延判定終了駅"
                testId="delay-end-stop-select"
                value={endStopId}
                stops={stops}
                disabled={!currentRouteId}
                onChange={(stopId) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    end_stop_id: stopId,
                  } as ConnectingLineDelayProperties)
                }
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            開始駅・終了駅を指定すると、その区間で遅延があるときのみ放送します。
          </p>
        </>
      ) : null}

      {/*
        乗換駅は section_scheduled / transfer_aware で使用する。
        whole_line では路線名のみを読み上げる純化されたモードのため非表示。

        ユーザ要望により、transfer_aware モードでは「乗換駅 → 乗換時間 (秒)」の
        順で並べる (乗換駅をどこにするか決めてから、その駅での乗換時間を入力するという
        UX の流れに合わせる)。section_scheduled モードでは乗換時間は表示されないため、
        この順序変更は実質的に transfer_aware モードだけに影響する。
      */}
      {showTransferStopField ? (
        <>
          <label>
            <span className={usesPatternsTable ? 'required' : undefined}>
              乗換駅{usesPatternsTable ? '' : ' (任意)'}
            </span>
            <StopSelect
              ariaLabel="乗換駅"
              testId="transfer-stop-select"
              value={transferStopId}
              stops={stops}
              disabled={!currentRouteId}
              onChange={(stopId) =>
                onChange({
                  ...props,
                  route_id: props.route_id ?? '',
                  delay_threshold_seconds: threshold,
                  transfer_stop_id: stopId,
                } as ConnectingLineDelayProperties)
              }
            />
          </label>
          <p className="hint" style={{ margin: '4px 0' }}>
            {usesPatternsTable
              ? '乗り換え先の発車時刻を取得する駅であり、放送文でも読み上げます。'
              : '放送文で読み上げる乗換駅です。未指定の場合は遅延判定開始駅を読み上げます。'}
          </p>
        </>
      ) : null}

      {/* 乗換時間は transfer_aware / departure_forecast モードのときに表示する */}
      {usesPatternsTable ? (
        <label>
          <span className="required">乗換時間 (秒)</span>
          <NumericInput
            min={0}
            step={1}
            aria-label="乗換時間 (秒)"
            data-testid="transfer-time-seconds-input"
            value={transferTimeSeconds}
            normalizeValue={clampNonNegativeInt}
            onChange={(nextTransferTime) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                transfer_time_seconds: nextTransferTime,
              } as ConnectingLineDelayProperties)
            }
          />
        </label>
      ) : null}

      {/*
        方面は section_scheduled / transfer_aware で使用する。

        whole_line では路線全体の遅延を見るため方面の限定は不要 → 非表示。

        ここの「方面」ドロップダウンは **判定用** (route-stops / stop-times API の
        direction_id クエリパラメータとして使われ、ダイヤ判定対象の方向を絞り込む)。
        放送文 (SSML の {direction_name} プレースホルダ) に差し込む方面文言は
        独立した自由入力テキストボックス (下の「方面 (案内文面用・任意)」) で
        設定する。
      */}
      {showDirectionField ? (
        <label>
          {/*
            画面表示ラベルは「方面 (判定用・任意)」、aria-label は既存テストとの
            互換性のため「方面」のままにする (E2E / AI エージェントの取り違え対策で
            旧来から付与している ariaLabel を変更すると下流のシナリオが壊れるため)。
          */}
          <span>方面 (判定用・任意)</span>

          <select
            aria-label="方面"
            // 画面トップの線区フィルタにも data-testid="direction-select" / aria-label="方面" の
            // <select> が存在するため、E2E や AI エージェントが取り違えないように
            // モーダル内の方面 <select> には別の testid を付与する。
            data-testid="content-direction-select"
            value={directionId}
            disabled={!currentRouteId}
            onChange={(e) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                direction_id: e.target.value,
              } as ConnectingLineDelayProperties)
            }
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (一覧にない)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      {/*
        案内文面用の方面文言 (自由入力テキスト)。
        判定用 direction_id とは独立した、放送文 (SSML の {direction_name}
        プレースホルダ) に差し込まれる任意テキスト。
        - section_scheduled / transfer_aware モードのときに使用する。
        - whole_line モードでは判定にも放送文にも使われない (バックエンドで無視) ため非表示。
      */}
      {showDirectionField ? (
        <>
          <label>
            <span>方面 (案内文面用・任意)</span>
            {/*
              テキストボックスには **駅名のみ** を入力する想定。
              放送文 (SSML) では Lambda 側で末尾に固定文言「方面行き」を結合してから
              {direction_name} プレースホルダへ展開する (例: 入力「千葉」→ 放送文「千葉方面行き」)。
              結合が行われることを視覚的に伝えるため、テキストボックスの右隣に
              固定ラベル <span>方面行き</span> を併記する。
            */}
            <div className="direction-name-input-row">
              <input
                type="text"
                aria-label="方面 (案内文面用)"
                data-testid="content-direction-name-input"
                value={directionName}
                placeholder="例: 千葉"
                onChange={(e) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    // 空文字は undefined にして DB / JSON にゴミを残さない
                    direction_name: e.target.value === '' ? undefined : e.target.value,
                  } as ConnectingLineDelayProperties)
                }
              />
              <span
                className="direction-name-suffix"
                data-testid="content-direction-name-suffix"
                aria-hidden="true"
              >
                方面行き
              </span>
            </div>
          </label>
          <p
            className="hint"
            style={{ margin: '4px 0' }}
            data-testid="connecting-delay-direction-name-hint"
          >
            {isDepartureForecast ? (
              <>
                発車見込み案内では、接続便の行先を取得できないときのみこの値を行先として読み上げます
                (<code>方面行き</code> は付与されません。例:「千葉」→「千葉」)。
                空欄で行先も取得できない場合は行先を読み上げません。
              </>
            ) : (
              <>
                放送文では入力値の末尾に <code>方面行き</code> が自動で付与されます (例:
                「千葉」→「千葉方面行き」)。 空欄なら方面は読み上げられません。
              </>
            )}
            判定用「方面 (判定用・任意)」とは独立して指定できます。
          </p>
        </>
      ) : null}

      {/*
        transfer_aware モードでは「遅れ分数の幅 × 放送粒度 × 放送パターン」を表形式で
        編集できるようにする (自列車遅延と同じ DelayPatternRow を再利用)。
        他モードでは従来通り単一の SSML テンプレート (delay_threshold_seconds と組合せ) を使う。
      */}
      {showDelayPatternsTable ? (
        <ConnectingLineDelayPatternsTable
          props={props}
          threshold={threshold}
          // Issue #439: 行ごとの音声プレビューに必要な情報を渡す。
          content={content}
          contentIndex={contentIndex}
          parentVoiceId={parentVoiceId}
          playbackRate={playbackRate}
          onPreviewSsml={onPreviewSsml}
          onChange={onChange}
        />
      ) : null}

      {/*
        単一 SSML テンプレートは whole_line / section_scheduled でのみ使用する。
        transfer_aware は delay_patterns 側の SSML テンプレートを使うため非表示にし、
        単一テンプレート (ssml_template) は properties に残さない設計とする。
      */}
      {showSingleSsmlField ? (
        <>
          {languages.includes('ja') ? (
            <label>
              {/*
               * 画面表示ラベルと aria-label は完全一致させること。
               * 1 つの放送地点に「自列車遅延」と「接続線遅延」の両方を設定するケースがあり、
               * 自列車遅延側にも SSML テンプレート系の <textarea> が存在するため、
               * E2E / AI エージェントが取り違えないように「(接続線遅延)」を必ず付与する。
               * 併せて data-testid="content-ssml-template" でも一意に取得できるようにする。
               */}
              <span>SSML テンプレート (接続線遅延)</span>
              <textarea
                rows={2}
                aria-label="SSML テンプレート (接続線遅延)"
                data-testid="content-ssml-template"
                // Issue #527: 空欄 = 定型文マスタへ委譲なので **必須にしない**。
                // 空欄のときは放送側が判定モード別の定型文マスタ ID
                // (`connecting_delay_whole_line` / `connecting_delay_section_scheduled`)
                // → `connecting_delay` → コード内既定の順に解決する (無音にならない)。
                value={props.ssml_template ?? ''}
                onChange={(e) =>
                  onChange({
                    ...props,
                    route_id: props.route_id ?? '',
                    delay_threshold_seconds: threshold,
                    ssml_template: e.target.value,
                  } as ConnectingLineDelayProperties)
                }
                /*
                判定モード別の placeholder。**実値の初期値と同じ定数**
                (`CONNECTING_SINGLE_SSML_DEFAULT`) をそのまま表示する。

                - whole_line: Lambda 側で from_station が常に空文字に強制されるため、
                  `{from_prefix}` / `{from_station}` / `{direction_name}` も空文字に展開される。
                  ユーザー混乱を避けるために placeholder からはこれらを除いた文面になっている。
                - section_scheduled: `{from_prefix}` が「<駅名>駅からお」に展開される。

                ※ SBI-2: 以前は `{delay_seconds}` (秒読み上げ) を例示していたが、
                  秒単位は音声パターンが 1 秒刻みで際限なく増えるため撤去し、
                  分単位 (`{delay_minutes_1}`) の既定文面へ差し替えた。
                  placeholder と実値を同じ定数から引くことで、
                  「薄文字の例示」と「保存される既定値」が食い違わなくなる。
              */
                /*
                  Issue #552: 薄字 (薄い文字) は「未入力時に実際に放送される文面」。
                  定型文設定 (`connecting_delay_whole_line` /
                  `connecting_delay_section_scheduled`) に登録があればその文面、
                  無ければコード内既定 (`CONNECTING_SINGLE_SSML_DEFAULT`) へ落ちる。
                */
                placeholder={effectiveSingleSsml}
              />
            </label>
          ) : null}
          <LocalizedTextFields
            label="SSML テンプレート (接続線遅延)"
            languages={languages}
            value={props.ssml_template_i18n}
            // Issue #552: 各言語の既定文面も定型文マスタ (判定モード別 ID) を反映する
            // (マスタ未登録の言語はコード内既定のまま)。
            defaults={resolveLocalizedDefaults(
              localizedConnectingSingleDefaults(singleSsmlMode),
              singleSsmlTemplateId,
              resolveDefault,
            )}
            // Issue #556: 既定文面は **薄字 (placeholder) 専用**。値として入れると
            // 定型文マスタの多言語編集が反映されなくなる (空欄 = マスタ委譲を守る)。
            defaultsAreValues={false}
            // Issue #492 (SBI-11): 翻訳元は同じコンテンツの日本語 SSML テンプレート。
            //
            // Issue #552: 未入力 (= マスタ委譲) のときは実効文面を翻訳元にする
            // (空文字だと「日本語から翻訳」ボタンが押せない)。
            sourceText={effectiveSingleSsml}
            translateTestId={`connecting-line-delay-${contentIndex}`}
            onChange={(value) =>
              onChange({
                ...props,
                route_id: props.route_id ?? '',
                delay_threshold_seconds: threshold,
                ssml_template_i18n: value,
              })
            }
          />
          {languages.includes('ja') ? (
            <p className="hint" style={{ margin: '4px 0' }}>
              SSML は<strong>空欄でも構いません</strong>。空欄のときは定型文設定
              (定型文設定画面で編集) の判定モード別デフォルト文面が使われます
              (薄字で表示されている文面)。定型文設定を編集すると、空欄のコンテンツすべての
              放送内容が一括で変わります。
            </p>
          ) : null}
          {/*
            Issue #439: 単一 SSML テンプレート欄の直下に音声プレビューを置く。
            プレビュー条件パネルの入力欄は判定モード別のプレースホルダ定義から
            決まるため、whole_line モードでは乗換駅・方面の入力欄が現れない
            (= 本番でも読み上げられない項目を触らせない)。
          */}
          {content ? (
            <AudioPreviewField
              content={content}
              parentVoiceId={parentVoiceId}
              playbackRate={playbackRate}
              onPreviewSsml={onPreviewSsml}
              testIdSuffix={`connecting-line-delay-${contentIndex}`}
              placeholders={
                isWholeLineMode
                  ? CONNECTING_WHOLE_LINE_PLACEHOLDERS
                  : CONNECTING_SECTION_SCHEDULED_PLACEHOLDERS
              }
              // 条件パネルの既定値はコンテンツ設定 (線名 / 乗換駅 / 方面) から導出する。
              // ユーザーが条件パネルで指定した値は上書きされない (`usePreviewSample`)。
              sampleOverrides={deriveContentSampleOverrides(content)}
              disabled={(props.ssml_template ?? '').trim() === ''}
              disabledReason="SSML を入力すると試聴できます"
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}
